import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { IconButton, TextField, Tooltip } from '@mui/material';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';

/* Moves one item, the way react-beautiful-dnd reports a drop. */
export const moveItem = (list, from, to) => {
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

/**
 * The board's swimlanes, in board order, as a list you can drag to reorder.
 *
 * Purely the editor: every change is handed to the parent's callbacks, which
 * talk to the server. A reorder shows at once and is undone if onReorder
 * rejects, so the list never claims an order the board does not have.
 */
const BoardStatusList = ({ statuses, busy, onReorder, onRename, onAdd, onDelete }) => {
  const [items, setItems] = useState(statuses);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  useEffect(() => {
    setItems(statuses);
  }, [statuses]);

  const handleDragEnd = async ({ source, destination }) => {
    if (!destination || destination.index === source.index) return;
    const previous = items;
    const next = moveItem(items, source.index, destination.index);
    setItems(next);
    try {
      await onReorder(next.map((status) => status.id));
    } catch {
      setItems(previous);
    }
  };

  const startEdit = (status) => {
    setEditingId(status.id);
    setDraft(status.title);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft('');
  };

  const commitEdit = async (status) => {
    const title = draft.trim();
    if (!title || title === status.title) {
      cancelEdit();
      return;
    }
    try {
      await onRename(status, title);
      cancelEdit();
    } catch {
      // the parent has already said why; keep the editor open to fix it
    }
  };

  const commitAdd = async () => {
    const title = newTitle.trim();
    if (!title) return;
    try {
      await onAdd(title);
      setNewTitle('');
      setAdding(false);
    } catch {
      // as above
    }
  };

  const editorKeys = (onEnter, onEscape) => (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onEnter();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onEscape();
    }
  };

  return (
    <div className="board-lanes">
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="board-lanes">
          {(dropProvided, dropSnapshot) => (
            <ol
              className={`board-lanes__list ${dropSnapshot.isDraggingOver ? 'is-over' : ''}`}
              ref={dropProvided.innerRef}
              {...dropProvided.droppableProps}
              aria-label="Board columns, in order"
            >
              {items.map((status, index) => (
                <Draggable
                  key={status.id}
                  draggableId={String(status.id)}
                  index={index}
                  isDragDisabled={busy || editingId !== null}
                >
                  {(dragProvided, dragSnapshot) => (
                    <li
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      className={`board-lane ${dragSnapshot.isDragging ? 'is-dragging' : ''}`}
                      data-testid="board-lane"
                    >
                      <span
                        className="board-lane__handle"
                        {...dragProvided.dragHandleProps}
                        aria-label={`Drag ${status.title} to reorder`}
                      >
                        <DragIndicatorRoundedIcon />
                      </span>
                      <span className="board-lane__pos">{index + 1}</span>

                      {editingId === status.id ? (
                        <div className="board-lane__editor">
                          <TextField
                            size="small"
                            fullWidth
                            autoFocus
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={editorKeys(() => commitEdit(status), cancelEdit)}
                            inputProps={{ 'aria-label': 'Column name' }}
                          />
                          <IconButton className="lane-btn is-confirm" onClick={() => commitEdit(status)} disabled={busy} aria-label="Save name">
                            <CheckRoundedIcon />
                          </IconButton>
                          <IconButton className="lane-btn" onClick={cancelEdit} aria-label="Cancel rename">
                            <CloseRoundedIcon />
                          </IconButton>
                        </div>
                      ) : (
                        <>
                          <span className="board-lane__title" onDoubleClick={() => startEdit(status)}>
                            {status.title}
                          </span>
                          <span className="board-lane__actions">
                            <Tooltip title="Rename">
                              <span>
                                <IconButton className="lane-btn" onClick={() => startEdit(status)} disabled={busy} aria-label={`Rename ${status.title}`}>
                                  <EditRoundedIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={items.length <= 1 ? 'A board needs at least one column' : 'Delete'}>
                              <span>
                                <IconButton
                                  className="lane-btn is-danger"
                                  onClick={() => onDelete(status)}
                                  disabled={busy || items.length <= 1}
                                  aria-label={`Delete ${status.title}`}
                                >
                                  <DeleteOutlineRoundedIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </span>
                        </>
                      )}
                    </li>
                  )}
                </Draggable>
              ))}
              {dropProvided.placeholder}
            </ol>
          )}
        </Droppable>
      </DragDropContext>

      {adding ? (
        <div className="board-lane is-new">
          <span className="board-lane__pos">{items.length + 1}</span>
          <div className="board-lane__editor">
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder="Column name, e.g. Blocked"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={editorKeys(commitAdd, () => { setAdding(false); setNewTitle(''); })}
              inputProps={{ 'aria-label': 'New column name' }}
            />
            <IconButton className="lane-btn is-confirm" onClick={commitAdd} disabled={busy || !newTitle.trim()} aria-label="Add column">
              <CheckRoundedIcon />
            </IconButton>
            <IconButton className="lane-btn" onClick={() => { setAdding(false); setNewTitle(''); }} aria-label="Cancel new column">
              <CloseRoundedIcon />
            </IconButton>
          </div>
        </div>
      ) : (
        <button type="button" className="board-lanes__add" onClick={() => setAdding(true)} disabled={busy}>
          <AddRoundedIcon />
          Add column
        </button>
      )}
    </div>
  );
};

export default BoardStatusList;
